import { useState } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Spinner,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { History, MessageSquare, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ChatService } from "@root/services";
import { getBaseUrl } from "@root/store/selectors/features";
import { formatDateTime } from "@root/utils/date-helper";
import { DATE_FORMATS } from "@root/constants";
import { CHAT_COLORS, CHAT_FONT } from "./chatStyles";

interface ChatHeaderProps {
  title: string;
  chatType: number;
  activeThreadId?: string | number | null;
  onNewChat: () => void;
  onOpenThread: (threadId: number) => void;
}

/** Top bar of a chat page: its title, "New chat" and the conversation history drawer. */
export const ChatHeader = ({
  title,
  chatType,
  activeThreadId,
  onNewChat,
  onOpenThread,
}: ChatHeaderProps) => {
  const { t } = useTranslation("common");
  const baseUrl = useSelector(getBaseUrl);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [threads, setThreads] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const openHistory = async () => {
    onOpen();
    setLoading(true);
    setFailed(false);
    const { data, error } = await new ChatService().getThreadsByChatType(
      baseUrl,
      chatType
    );
    setThreads(error ? [] : data?.rows ?? []);
    setFailed(Boolean(error));
    setLoading(false);
  };

  const pick = (threadId: number) => {
    onClose();
    onOpenThread(threadId);
  };

  return (
    <>
      <Flex
        flexShrink={0}
        h="56px"
        px={{ base: "16px", lg: "32px" }}
        align="center"
        justify="space-between"
        borderBottom={`1px solid ${CHAT_COLORS.border}`}
        bg="white"
      >
        <Text fontSize="16px" fontWeight="600" color={CHAT_COLORS.text} noOfLines={1}>
          {title}
        </Text>
        <HStack spacing={2}>
          <Button
            size="sm"
            variant="ghost"
            color={CHAT_COLORS.muted}
            leftIcon={<History size={16} />}
            onClick={openHistory}
          >
            {t("chatThreads.history")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            borderColor={CHAT_COLORS.border}
            color={CHAT_COLORS.text}
            leftIcon={<Plus size={16} />}
            onClick={onNewChat}
          >
            {t("chatThreads.newChat")}
          </Button>
        </HStack>
      </Flex>

      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
        <DrawerOverlay />
        {/* The theme gives drawers no background */}
        <DrawerContent bg="white" fontFamily={CHAT_FONT}>
          <DrawerHeader borderBottomWidth="1px">
            <HStack justify="space-between" align="center">
              <Text fontSize="18px" fontWeight="600" color={CHAT_COLORS.brand}>
                {t("chatThreads.title")}
              </Text>
              <DrawerCloseButton position="static" />
            </HStack>
          </DrawerHeader>
          <DrawerBody px={3} pt={3}>
            {loading ? (
              <Spinner size="sm" m={3} color={CHAT_COLORS.brand} />
            ) : failed ? (
              <Text fontSize="14px" color="red.500" p={3}>
                {t("chatThreads.loadFailed")}
              </Text>
            ) : threads.length === 0 ? (
              <VStack spacing={2} py={10} color={CHAT_COLORS.subtle}>
                <MessageSquare size={28} />
                <Text fontSize="14px" color={CHAT_COLORS.muted}>
                  {t("chatThreads.empty")}
                </Text>
              </VStack>
            ) : (
              <VStack align="stretch" spacing={1}>
                {threads.map((thread) => {
                  const active = Number(activeThreadId) === Number(thread.id);
                  return (
                    <Box
                      key={thread.id}
                      as="button"
                      textAlign="left"
                      px={3}
                      py="10px"
                      borderRadius="8px"
                      bg={active ? "#EEF4FB" : "transparent"}
                      _hover={{ bg: active ? "#EEF4FB" : "#F8FAFC" }}
                      onClick={() => pick(Number(thread.id))}
                    >
                      <Text
                        fontSize="14px"
                        fontWeight={active ? 600 : 500}
                        color={CHAT_COLORS.text}
                        noOfLines={2}
                      >
                        {thread.title}
                      </Text>
                      <Text fontSize="12px" color={CHAT_COLORS.subtle} mt="2px">
                        {formatDateTime(thread.createdAt, DATE_FORMATS.DATE_AND_TIME)}
                      </Text>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
